/**
 * Pulls the hero up under a transparent header so the nav floats over it.
 *
 * The theme's own homepage heroes (page-header-image-banner, page-header-logo-banner)
 * each reimplement this inline. This is the same geometry, scoped to the block:
 *
 *   - Only engages when the header is actually transparent (data-transparent) AND
 *     this hero is the first section after the header. Otherwise the offset stays
 *     0 and the hero renders in normal flow.
 *   - Offsets by the header height only, not the announcement bar. The announcement
 *     bar is a sibling *before* header.page-header with its own solid background,
 *     so pulling under it would hide it.
 *   - Writes the measured height to --hero-split-header-offset; the stylesheet owns
 *     what to do with it (negative margin + matching content padding).
 *
 * It also owns the scroll hand-off. page-header.liquid renders
 * wt-header--transparent statically, so something has to take it off once the hero
 * has scrolled away or the nav stays transparent over the whole page. The built-in
 * heroes each do this with their own IntersectionObserver; this mirrors that, plus
 * the wt-header__logo--show swap — while transparent, critical.css sets
 * .wt-header__logo to opacity 0, so the logo is invisible without that class.
 */
class HeroSplit extends HTMLElement {
  constructor() {
    super();
    this.hero = this.querySelector('.wt-hero-split');
    this.overlayClass = 'wt-hero-split--under-header';
    this.sectionClass = 'hero-split-under-header';
    this.transparentClass = 'wt-header--transparent';
    this.showLogoClass = 'wt-header__logo--show';
  }

  connectedCallback() {
    this.update();

    // rootMargin bakes in the viewport height, so the observer has to be rebuilt
    // on resize rather than just remeasured.
    this._onResize = () => {
      this.teardownScrollToggle();
      this.update();
    };
    window.addEventListener('resize', this._onResize, { passive: true });

    // The header's height depends on webfonts and the logo image; remeasure once
    // those have settled so the offset doesn't lock in a too-short value.
    if (document.fonts?.ready) {
      document.fonts.ready.then(() => this._onResize());
    }

    if (window.Shopify?.designMode) {
      this._onSectionEvent = () => this._onResize();
      document.addEventListener('shopify:section:load', this._onSectionEvent);
      document.addEventListener('shopify:section:unload', this._onSectionEvent);
      document.addEventListener('shopify:section:reorder', this._onSectionEvent);
    }
  }

  disconnectedCallback() {
    this.teardownScrollToggle();

    if (this._onResize) {
      window.removeEventListener('resize', this._onResize);
      this._onResize = null;
    }

    if (this._onSectionEvent) {
      document.removeEventListener('shopify:section:load', this._onSectionEvent);
      document.removeEventListener('shopify:section:unload', this._onSectionEvent);
      document.removeEventListener('shopify:section:reorder', this._onSectionEvent);
      this._onSectionEvent = null;
    }
  }

  /**
   * True when this hero is the first thing rendered below the header.
   *
   * Two placements are valid and reached differently:
   *   - In a template (templates/index.json) the hero renders inside
   *     <main id="root">, so it must be main's first section.
   *   - In a section group it's a direct sibling of header.page-header.
   * Anything else (hero further down the page) must not overlay.
   */
  isFirstSection() {
    const section = this.closest('.shopify-section') || this.closest('section');
    if (!section) return false;

    const main = document.querySelector('main#root');
    if (main?.contains(section)) {
      const first = main.querySelector('.shopify-section, section');
      return first === section;
    }

    const pageHeader = document.querySelector('header.page-header');
    if (!pageHeader) return false;

    let sibling = pageHeader.nextElementSibling;
    while (sibling) {
      if (sibling === section) return true;
      // Another section reached first — this hero isn't the one under the nav.
      if (sibling.classList.contains('shopify-section') || sibling.tagName.toLowerCase() === 'section') {
        return false;
      }
      sibling = sibling.nextElementSibling;
    }

    return false;
  }

  isOverlayEnabled() {
    const header = document.querySelector('.wt-header');
    return Boolean(header?.dataset.transparent) && this.isFirstSection();
  }

  /** The section wrapper that carries the pull-up margin. */
  getSection() {
    return this.closest('.shopify-section') || this.closest('section');
  }

  update() {
    if (!this.hero) return;

    const section = this.getSection();

    if (!this.isOverlayEnabled()) {
      this.hero.classList.remove(this.overlayClass);
      this.hero.style.removeProperty('--hero-split-header-offset');
      section?.classList.remove(this.sectionClass);
      section?.style.removeProperty('--hero-split-header-offset');
      this.teardownScrollToggle();
      return;
    }

    const header = document.querySelector('header.page-header');
    const offset = header ? header.offsetHeight : 0;

    this.hero.classList.add(this.overlayClass);
    this.hero.style.setProperty('--hero-split-header-offset', `${offset}px`);

    if (section) {
      section.classList.add(this.sectionClass);
      section.style.setProperty('--hero-split-header-offset', `${offset}px`);
    }

    this.setupScrollToggle();
  }

  /**
   * Transparent while any part of the hero is still behind the nav; solid after.
   *
   * The root margin crops the viewport to the header strip, so "intersecting"
   * means "hero is still under the nav" rather than "hero is somewhere on screen".
   * Without that, a hero taller than the viewport would keep the nav transparent
   * long after the nav had left the image.
   */
  setupScrollToggle() {
    if (this._headerObserver) return;

    const header = document.querySelector('.wt-header');
    if (!header) return;

    const headerHeight = document.querySelector('header.page-header')?.offsetHeight || 0;

    this._headerObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          this.setHeaderTransparent(entry.isIntersecting);
        });
      },
      { root: null, rootMargin: `0px 0px -${Math.max(window.innerHeight - headerHeight, 0)}px 0px`, threshold: 0 },
    );

    this._headerObserver.observe(this.hero);
  }

  setHeaderTransparent(isTransparent) {
    const header = document.querySelector('.wt-header');
    if (!header) return;

    const logoWrapper = header.querySelector('.wt-header__logo');

    header.classList.toggle(this.transparentClass, isTransparent);
    // Transparent hides the logo by default; --show is what reveals it again.
    logoWrapper?.classList.toggle(this.showLogoClass, isTransparent);
  }

  teardownScrollToggle() {
    if (!this._headerObserver) return;

    this._headerObserver.disconnect();
    this._headerObserver = null;

    // Restore the state page-header.liquid renders, so a hero removed in the editor
    // (or a resize rebuild) never strands the nav mid-toggle.
    const header = document.querySelector('.wt-header');
    if (header?.dataset.transparent) this.setHeaderTransparent(true);
  }
}

if (!customElements.get('hero-split')) {
  customElements.define('hero-split', HeroSplit);
}
